
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

// Define proper interface for the subscriber data
interface Subscriber {
  email: string;
  status: string;
}

export function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const subscriberId = searchParams.get('subscriber_id');
  const campaignId = searchParams.get('campaign_id');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [subscriber, setSubscriber] = useState<Subscriber | null>(null);
  const [reason, setReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [resubscribe, setResubscribe] = useState(false);
  
  useEffect(() => {
    const fetchSubscriber = async () => {
      if (!subscriberId) {
        setError('Invalid unsubscribe link. Missing subscriber information.');
        setLoading(false);
        return;
      }
      
      try {
        // Using mock data for development
        // In a real app, you'd query the database
        const mockSubscriber = {
          email: "subscriber@example.com",
          status: "active"
        };
        
        setSubscriber(mockSubscriber);
        
        // If already unsubscribed, set success state
        if (mockSubscriber.status === 'unsubscribed') {
          setSuccess(true);
        }
        
        // Actual implementation would be:
        // const { data, error: fetchError } = await supabase
        //   .from('newsletter_subscriptions')
        //   .select('email, status')
        //   .eq('id', subscriberId)
        //   .single();
        // 
        // if (fetchError) throw fetchError;
        // if (!data) throw new Error('Subscriber not found');
        // 
        // setSubscriber(data);
        // 
        // // If already unsubscribed, set success state
        // if (data.status === 'unsubscribed') {
        //   setSuccess(true);
        // }
      } catch (error) {
        setError('Could not find your subscription information. The link may be invalid or expired.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSubscriber();
  }, [subscriberId]);
  
  const handleUnsubscribe = async () => {
    if (!subscriberId) return;
    
    setLoading(true);
    try {
      // Mock successful unsubscribe for development
      // In a real app, you'd update the database
      
      setTimeout(() => {
        setSuccess(true);
        toast.success('You have been successfully unsubscribed');
        setLoading(false);
      }, 1000);
      
      // Actual implementation would be:
      // // Update subscriber status
      // const { error: updateError } = await supabase
      //   .from('newsletter_subscriptions')
      //   .update({
      //     status: 'unsubscribed',
      //     updated_at: new Date().toISOString()
      //   })
      //   .eq('id', subscriberId);
      // 
      // if (updateError) throw updateError;
      // 
      // // Record unsubscribe event with reason
      // if (campaignId) {
      //   await supabase.from('newsletter_events').insert({
      //     subscriber_id: subscriberId,
      //     campaign_id: campaignId,
      //     event_type: 'unsubscribe',
      //     event_data: {
      //       reason: reason === 'other' ? otherReason : reason,
      //       timestamp: new Date().toISOString()
      //     }
      //   });
      // }
      // 
      // setSuccess(true);
      // toast.success('You have been successfully unsubscribed');
    } catch (error) {
      toast.error('Failed to unsubscribe. Please try again.');
      setError('There was an error processing your request. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleResubscribe = async () => {
    if (!subscriberId) return;
    
    setLoading(true);
    try {
      // Mock successful resubscribe for development
      setTimeout(() => {
        toast.success('You have been resubscribed to our newsletter');
        setResubscribe(true);
        
        // Redirect to home after a short delay
        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
        
        setLoading(false);
      }, 1000);
      
      // Actual implementation would be:
      // // Update subscriber status back to active
      // const { error: updateError } = await supabase
      //   .from('newsletter_subscriptions')
      //   .update({
      //     status: 'active',
      //     updated_at: new Date().toISOString()
      //   })
      //   .eq('id', subscriberId);
      // 
      // if (updateError) throw updateError;
      // 
      // // Record resubscribe event
      // if (campaignId) {
      //   await supabase.from('newsletter_events').insert({
      //     subscriber_id: subscriberId,
      //     campaign_id: campaignId,
      //     event_type: 'resubscribe',
      //     event_data: {
      //       timestamp: new Date().toISOString()
      //     }
      //   });
      // }
      // 
      // toast.success('You have been resubscribed to our newsletter');
      // setResubscribe(true);
      // 
      // // Redirect to home after a short delay
      // setTimeout(() => {
      //   window.location.href = '/';
      // }, 3000);
    } catch (error) {
      toast.error('Failed to resubscribe. Please try again.');
      setError('There was an error processing your request. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
        <p>Processing your request...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <Card className="max-w-md mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertCircle />
            Error
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p>{error}</p>
        </CardContent>
        <CardFooter>
          <Button onClick={() => window.location.href = '/'}>
            Return to Homepage
          </Button>
        </CardFooter>
      </Card>
    );
  }
  
  if (success && !resubscribe) {
    return (
      <Card className="max-w-md mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-600">
            <CheckCircle />
            Unsubscribed
          </CardTitle>
          <CardDescription>
            You have been successfully unsubscribed from our newsletter.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="mb-4">
            Your email <span className="font-medium">{subscriber?.email}</span> will no longer receive emails from us.
          </p>
          <p className="text-sm text-muted-foreground mb-6">
            Changed your mind? You can resubscribe at any time.
          </p>
          <Button onClick={handleResubscribe} variant="outline" className="w-full">
            Resubscribe
          </Button>
        </CardContent>
        <CardFooter className="flex justify-center">
          <Button onClick={() => window.location.href = '/'} variant="ghost">
            Return to Homepage
          </Button>
        </CardFooter>
      </Card>
    );
  }
  
  if (resubscribe) {
    return (
      <Card className="max-w-md mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-600">
            <CheckCircle />
            Welcome Back!
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p>
            You have been successfully resubscribed to our newsletter. You'll continue to receive
            updates at <span className="font-medium">{subscriber?.email}</span>.
          </p>
        </CardContent>
        <CardFooter className="flex justify-center">
          <Button onClick={() => window.location.href = '/'}>
            Return to Homepage
          </Button>
        </CardFooter>
      </Card>
    );
  }
  
  return (
    <Card className="max-w-md mx-auto">
      <CardHeader>
        <CardTitle>Unsubscribe</CardTitle>
        <CardDescription>
          We're sorry to see you go. Please confirm your unsubscribe request.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p>
          Your email: <span className="font-medium">{subscriber?.email}</span>
        </p>
        
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Would you mind telling us why you're leaving?</h3>
          
          <div className="space-y-1">
            {['Too many emails', 'Content not relevant', 'Never signed up', 'other'].map((option) => (
              <div key={option} className="flex items-center space-x-2">
                <Checkbox 
                  id={option} 
                  checked={reason === option}
                  onCheckedChange={() => setReason(option)}
                />
                <label 
                  htmlFor={option}
                  className="text-sm cursor-pointer"
                >
                  {option === 'other' ? 'Other reason' : option}
                </label>
              </div>
            ))}
          </div>
          
          {reason === 'other' && (
            <Textarea
              placeholder="Please tell us more..."
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
              className="h-20"
            />
          )}
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button variant="ghost" onClick={() => window.location.href = '/'}>
          Cancel
        </Button>
        <Button onClick={handleUnsubscribe} disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            'Confirm Unsubscribe'
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}
